import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';
import { getErrorMessage } from '@/api/client';
import { suppliersApi } from '@/api/suppliers';
import { useToast }     from '@/providers/ToastProvider';
import { useAuth }      from '@/providers/AuthProvider';
import {
  Button, FormField, Input, Textarea, Card, CardContent,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Badge, ConfirmDialog, Spinner,
} from '@/components/ui';
import { Plus, Pencil, Trash2, X, Check, ChevronDown, ChevronRight, Search } from 'lucide-react';
import type { Supplier } from '@/types';

// ─── Schema ──────────────────────────────────────────────────────────────────
const schema = z.object({
  name:         z.string().min(1, 'required'),
  contact_name: z.string().nullable().optional(),
  email:        z.string().email().nullable().or(z.literal('')).optional(),
  phone:        z.string().nullable().optional(),
  address:      z.string().nullable().optional(),
  notes:        z.string().nullable().optional(),
  iban:         z.string().max(34).nullable().optional(),
});
type FormValues = z.infer<typeof schema>;

export default function SuppliersPage() {
  const { t }         = useTranslation();
  const queryClient   = useQueryClient();
  const { toast } = useToast();
  const { isAdmin }   = useAuth();

  const [editing, setEditing]       = useState<Supplier | null>(null);
  const [creating, setCreating]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Supplier | null>(null);
  const [expandedSupplierId, setExpandedSupplierId] = useState<string | null>(null);
  const [search, setSearch]         = useState('');

  const formOpen = creating || !!editing;
  const columnCount = isAdmin ? 8 : 7;

  // ── Query ─────────────────────────────────────────────────────
  const { data: suppliers = [], isLoading } = useQuery({
    queryKey: ['suppliers'],
    queryFn:  () => suppliersApi.list({ page: 1, limit: 200 }).then(r => r.data),
  });

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.contact_name ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const {
    data: supplierIngredients = [],
    isLoading: isIngredientsLoading,
    error: supplierIngredientsError,
  } = useQuery({
    queryKey: ['supplier-ingredients', expandedSupplierId],
    queryFn: () => suppliersApi.getIngredients(expandedSupplierId as string),
    enabled: !!expandedSupplierId,
  });

  // ── Form ──────────────────────────────────────────────────────────────────
  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', contact_name: '', email: '', phone: '', address: '', notes: '', iban: '' },
  });

  const openCreate = () => {
    reset({ name: '', contact_name: '', email: '', phone: '', address: '', notes: '', iban: '' });
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (s: Supplier) => {
    reset({
      name:         s.name,
      contact_name: s.contact_name ?? '',
      email:        s.email ?? '',
      phone:        s.phone ?? '',
      address:      s.address ?? '',
      notes:        s.notes ?? '',
      iban:         s.iban ?? '',
    });
    setCreating(false);
    setEditing(s);
  };

  const closeForm = () => { setCreating(false); setEditing(null); };

  const toggleExpandedSupplier = (supplierId: string) => {
    setExpandedSupplierId(current => current === supplierId ? null : supplierId);
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: suppliersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast(t('suppliers.created'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormValues }) => suppliersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast(t('suppliers.updated'), 'success');
      closeForm();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: suppliersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      toast(t('suppliers.deleted'), 'success');
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const onSubmit = (data: FormValues) => {
    const clean = {
      ...data,
      contact_name: data.contact_name || null,
      email:        data.email || null,
      phone:        data.phone || null,
      address:      data.address || null,
      notes:        data.notes || null,
      iban:         data.iban || null,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: clean });
    } else {
      createMutation.mutate(clean);
    }
  };

  return (
    <div>
      {/* ── Sticky top bar ────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 lg:-mx-8 mb-6 bg-background border-b">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 md:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold tracking-tight">{t('nav.suppliers')}</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('common.search')}
                className="pl-9 w-48 sm:w-64"
              />
            </div>
            {isAdmin && (
              <Button size="sm" onClick={openCreate} disabled={formOpen}>
                <Plus size={14} /> {t('suppliers.newSupplier')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Form */}
        {formOpen && isAdmin && (
          <Card className="lg:col-span-1 order-0 lg:order-1">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{editing ? t('suppliers.editSupplier') : t('suppliers.newSupplier')}</h3>
                <Button variant="ghost" size="icon" onClick={closeForm}><X size={14} /></Button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-3">
                <FormField label={t('common.name')} error={errors.name?.message} required>
                  <Input {...register('name')} />
                </FormField>
                <FormField label={t('suppliers.contactName')} error={errors.contact_name?.message}>
                  <Input {...register('contact_name')} />
                </FormField>
                <FormField label={t('suppliers.email')} error={errors.email?.message}>
                  <Input type="email" {...register('email')} />
                </FormField>
                <FormField label={t('suppliers.phone')} error={errors.phone?.message}>
                  <Input {...register('phone')} />
                </FormField>
                <FormField label={t('suppliers.address')} error={errors.address?.message}>
                  <Textarea rows={2} {...register('address')} />
                </FormField>
                <FormField label={t('common.notes')} error={errors.notes?.message}>
                  <Textarea rows={2} {...register('notes')} />
                </FormField>
                <FormField label={t('suppliers.iban')} error={errors.iban?.message}>
                  <Input {...register('iban')} placeholder="e.g. ES91 2100 0418 4502 0005 1332" />
                </FormField>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={isSubmitting} className="flex-1">
                    <Check size={14} /> {isSubmitting ? t('common.saving') : t('common.save')}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* List */}
        <div className={formOpen ? 'lg:col-span-2' : 'lg:col-span-3'}>
          {isLoading ? (
            <Spinner className="py-16" />
          ) : suppliers.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground">
                {t('suppliers.empty')}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12" />
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead className="hidden sm:table-cell">{t('suppliers.contactName')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('suppliers.email')}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('suppliers.phone')}</TableHead>
                    <TableHead className="hidden lg:table-cell">{t('suppliers.iban')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    {isAdmin && <TableHead className="text-right">{t('common.actions')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSuppliers.map(s => (
                    <Fragment key={s.id}>
                      <TableRow className="cursor-pointer" onClick={() => toggleExpandedSupplier(s.id)}>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); toggleExpandedSupplier(s.id); }}
                            aria-label={t('suppliers.toggleIngredients')}
                          >
                            {expandedSupplierId === s.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground">{s.contact_name ?? '—'}</TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">{s.email ?? '—'}</TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">{s.phone ?? '—'}</TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground font-mono text-xs">{s.iban ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant={s.is_active ? 'success' : 'secondary'}>
                            {s.is_active ? t('common.active') : t('common.inactive')}
                          </Badge>
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEdit(s)}>
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="ghost" size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setDeleteTarget(s)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                      {expandedSupplierId === s.id && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={columnCount} className="py-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between gap-3">
                                <p className="text-sm font-medium text-foreground">
                                  {t('suppliers.connectedIngredients')} ({supplierIngredients.length})
                                </p>
                              </div>
                              {isIngredientsLoading ? (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Spinner size="sm" />
                                  <span>{t('suppliers.loadingIngredients')}</span>
                                </div>
                              ) : supplierIngredientsError ? (
                                <p className="text-sm text-destructive">{getErrorMessage(supplierIngredientsError)}</p>
                              ) : supplierIngredients.length === 0 ? (
                                <p className="text-sm text-muted-foreground">{t('suppliers.noConnectedIngredients')}</p>
                              ) : (
                                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                  {supplierIngredients.map(ingredient => (
                                    <div key={ingredient.id} className="rounded-md border border-border bg-background px-3 py-2">
                                      <p className="font-medium text-foreground">{ingredient.name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {ingredient.category_name ?? '—'}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('suppliers.deleteTitle')}
        description={t('suppliers.deleteDescription', { name: deleteTarget?.name })}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
